import { Box, Button, Typography, Divider } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ChainLevel } from './types';
import { PositionCell } from './PositionCell';
import { palette } from 'src/theme/style/palette';

interface Props {
    level: ChainLevel;
    currency: string;
    levelCost: string | number;
}

/**
 * Renders one chain level:
 *  - mentor centered at top
 *  - child slots in a row below
 *  - "Перейти к столу" button (from mentor link)
 */
export const ChainLevelView = ({ level, currency, levelCost }: Props) => {
    const mentor = level.positions[0];
    const mentorLink =
        mentor && typeof mentor !== 'boolean' ? mentor.link : undefined;

    const childSlots = level.positions.slice(1);

    return (
        <Box sx={{ display: 'flex', width: '100%', minWidth: 0, height: '100%' }}>
            <Box sx={{
                width: '100%',
                minWidth: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}>
                {/* Level label */}
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        textAlign: 'center',
                        color: palette.blue,
                        mb: 1,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                        fontSize: '0.95rem',
                        fontWeight: 500
                    }}
                >
                    {level.name_program} {level.name_level}
                </Typography>

                {/* Mentor */}
                <Box display="flex" justifyContent="center" mb={0.5}>
                    <PositionCell position={mentor} isMentor currency={currency} />
                </Box>

                {/* Child slots */}
                <Box display="flex" justifyContent="center" alignItems="flex-start" gap={1} mb={1.5} flexWrap="wrap">
                    {childSlots.map((pos, idx) => (
                        <PositionCell key={idx} position={pos} isMentor={false}
                            reward={level.rewards[idx]} slotIndex={idx + 1} currency={currency} />
                    ))}
                </Box>

                <RewardLegend />

                <Divider sx={{ mb: 1.5 }} />

                <Box sx={{ mt: 'auto' }}>
                    {mentorLink ? (
                        <Button variant="contained" fullWidth href={mentorLink} target="_blank"
                            component="a" size="small"
                            startIcon={<OpenInNewIcon sx={{ fontSize: '0.9rem' }} />}
                            sx={{ textTransform: 'none', fontSize: '0.8rem', py: 0.75 }}>
                            Перейти к столу
                        </Button>
                    ) : (
                        <Button variant="outlined" fullWidth disabled size="small"
                            sx={{ textTransform: 'none', fontSize: '0.8rem', py: 0.75 }}>
                            Перейти к столу
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

// ─── Reward legend ────────────────────────────────────────────────────────

const LEGEND = [
    // { color: '#F59E0B', label: 'Основное' },
    { color: '#3B82F6', label: 'Реф.' },
    { color: '#A855F7', label: 'Выплата' },
    { color: '#44A437', label: 'Апгрейд' },
    { color: '#FF6B2B', label: 'Клон' },
    // { color: '#FFD700', label: 'Спец.' },
];

const RewardLegend = () => (
    <Box
        display="flex"
        flexWrap="wrap"
        gap={1.1}
        mb={1.5}
        justifyContent="center"
    >
        {LEGEND.map((l) => (
            <Box key={l.label} display="flex" alignItems="center" gap={0.4}>
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: l.color,
                        flexShrink: 0,
                    }}
                />
                <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.82rem', color: 'text.main' }}>
                    {l.label}
                </Typography>
            </Box>
        ))}
    </Box>
);